import { apiFetch } from "@/lib/api-url"
// ============================================================
// Browser HTTP client for the wacrm API server.
//
// All requests go to the backend at VITE_API_URL (see src/lib/api-url.ts),
// cross-origin with credentials so the httpOnly session
// cookies attached. The server refreshes an expired access token
// transparently, so callers never deal with tokens.
//
// Two calling styles:
//
//   const contacts = await api.get<Contact[]>('/contacts', { query: { search } })   // throws ApiError
//   const { data, error } = await apiResult(api.get<Contact[]>('/contacts'))         // never throws
//
// `apiResult` mirrors the `{ data, error }` shape the components used
// with supabase-js, so their error handling stays unchanged.
// ============================================================

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export type QueryValue = string | number | boolean | null | undefined | (string | number)[];

export interface RequestOptions {
  query?: Record<string, QueryValue>;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const url = path.startsWith("/api/") ? path : `/api${path.startsWith("/") ? "" : "/"}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value)) {
      if (value.length > 0) params.set(key, value.join(","));
    } else {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json", ...opts.headers };
  let body: BodyInit | undefined;
  if (opts.body instanceof FormData || opts.body instanceof Blob) {
    body = opts.body;
  } else if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.body);
  }

  const res = await apiFetch(buildUrl(path, opts.query), {
    method,
    headers,
    body,
    credentials: "include",
    cache: "no-store",
    signal: opts.signal,
  });

  const text = await res.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  if (!res.ok) {
    const obj = (payload && typeof payload === "object" ? payload : {}) as {
      error?: unknown;
      message?: unknown;
      code?: unknown;
    };
    const errField = obj.error;
    const message =
      typeof errField === "string"
        ? errField
        : errField && typeof errField === "object" && typeof (errField as { message?: unknown }).message === "string"
          ? (errField as { message: string }).message
          : typeof obj.message === "string"
            ? obj.message
            : `Request failed (${res.status})`;
    const code =
      typeof obj.code === "string"
        ? obj.code
        : errField && typeof errField === "object" && typeof (errField as { code?: unknown }).code === "string"
          ? (errField as { code: string }).code
          : undefined;
    throw new ApiError(message, res.status, code, payload);
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, opts?: Omit<RequestOptions, "body">) => request<T>("GET", path, opts),
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, "body">) =>
    request<T>("POST", path, { ...opts, body }),
  put: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, "body">) =>
    request<T>("PUT", path, { ...opts, body }),
  patch: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, "body">) =>
    request<T>("PATCH", path, { ...opts, body }),
  delete: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, "body">) =>
    request<T>("DELETE", path, { ...opts, body }),
};

/** Error shape returned by `apiResult` — the fields components read. */
export interface ServiceError {
  message: string;
  code?: string;
  status?: number;
  details?: unknown;
  hint?: string;
}

export type ServiceResult<T> = { data: T; error: null } | { data: null; error: ServiceError };

/** Settle a request into `{ data, error }` instead of throwing. */
export async function apiResult<T>(promise: Promise<T>): Promise<ServiceResult<T>> {
  try {
    return { data: await promise, error: null };
  } catch (err) {
    if (err instanceof ApiError) {
      return {
        data: null,
        error: { message: err.message, code: err.code, status: err.status, details: err.details },
      };
    }
    return { data: null, error: { message: err instanceof Error ? err.message : "Request failed" } };
  }
}
